import {
  addBackLink,
  addTitleRow,
  applyColumnLayout,
  BODY_FONT,
  HEADER_FONT_BOLD_11,
  hideColumnsAfter,
  hideRowsThrough,
  NA_FILL,
  setCell,
  setupSheetView,
  THIN,
  THIN_BLACK,
  VALUE_FILL,
} from "./detailSheetCommon";

export const SHEET_NAME = "Other stakeholder group";

const COL_START = 4;
const COL_END = 14;

const HEADER_ROW_1 = 9;
const HEADER_ROW_2 = 10;
const TEMPLATE_ROW = 11;
const SPACER_ROW = 12;
const DATA_START_ROW = 13;

function formatFyShort(endYear) {
  const fy = Number(endYear);
  if (!fy) return "";
  return `${fy - 1}-${String(fy).slice(-2)}`;
}

function safeText(value, fallback = "") {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
}

function safeNum(value, fallback = "") {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
}

function resolveGrievanceMechanism(weblink, explicitValue) {
  const explicit = safeText(explicitValue);
  if (explicit) return explicit;
  return safeText(weblink) ? "Yes" : "";
}

function normalizeItems(sectionA) {
  const transparency = sectionA?.transparencyAndDisclosures || {};
  const current = transparency?.currentFYComplaints?.other || {};
  const previous = transparency?.previousFYComplaints?.other || {};
  const raw =
    transparency?.otherStakeholderGroups ||
    transparency?.otherStakeholderGroup ||
    [];

  const withOtherName = (item) => ({
    ...item,
    stakeholderGroup: safeText(item?.stakeholderGroup || item?.name, "Other"),
  });

  if (Array.isArray(raw) && raw.length) {
    return raw.map((item) =>
      withOtherName({
        ...item,
        currentFinancialYear: item?.currentFinancialYear || {
          filed: item?.filed,
          pending: item?.pending,
          remarks: item?.remarks,
        },
        previousFinancialYear: item?.previousFinancialYear || {},
      })
    );
  }

  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return [withOtherName(raw)];
  }

  return [
    withOtherName({
      stakeholderGroup: "Other",
      grievanceRedressalWeblink: current?.grievanceRedressalWeblink,
      grievanceMechanism: current?.grievanceMechanism,
      currentFinancialYear: {
        filed: current?.filed,
        pending: current?.pending,
        remarks: current?.remarks,
      },
      previousFinancialYear: {
        filed: previous?.filed,
        pending: previous?.pending,
        remarks: previous?.remarks,
      },
      naReason: current?.naReason || previous?.naReason,
    }),
  ];
}

function borderComplaintsHeaderRow1(ws, row) {
  ws.getCell(row, COL_START).border = { top: THIN, left: THIN, right: THIN };
  ws.getCell(row, 5).border = { top: THIN, left: THIN, right: THIN };
  ws.getCell(row, 6).border = { top: THIN, left: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 7).border = { top: THIN, left: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 8).border = { top: THIN, left: THIN, bottom: THIN };
  ws.getCell(row, 9).border = { top: THIN, bottom: THIN };
  ws.getCell(row, 10).border = { top: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 11).border = { top: THIN, left: THIN, bottom: THIN };
  ws.getCell(row, 12).border = { top: THIN, bottom: THIN };
  ws.getCell(row, 13).border = { top: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, COL_END).border = { top: THIN, left: THIN, right: THIN };
}

function borderComplaintsHeaderRow2(ws, row) {
  ws.getCell(row, COL_START).border = { left: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 5).border = { left: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 6).border = { left: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 7).border = { left: THIN, right: THIN, bottom: THIN };
  [8, 9, 10, 11, 12, 13].forEach((col) => {
    ws.getCell(row, col).border = THIN_BLACK;
  });
  ws.getCell(row, COL_END).border = { left: THIN, right: THIN, bottom: THIN };
}

function borderDataRow(ws, row) {
  ws.getCell(row, COL_START).border = THIN_BLACK;
  ws.getCell(row, 5).border = THIN_BLACK;
  ws.getCell(row, 6).border = THIN_BLACK;
  ws.getCell(row, 7).border = THIN_BLACK;
  ws.getCell(row, 8).border = THIN_BLACK;
  ws.getCell(row, 9).border = THIN_BLACK;
  ws.getCell(row, 10).border = THIN_BLACK;
  ws.getCell(row, 11).border = THIN_BLACK;
  ws.getCell(row, 12).border = THIN_BLACK;
  ws.getCell(row, 13).border = THIN_BLACK;
  ws.getCell(row, COL_END).border = THIN_BLACK;
}

function addSpacerRow(ws) {
  ws.getCell(SPACER_ROW, COL_START).border = { top: THIN, left: THIN, bottom: THIN };
  ws.getCell(SPACER_ROW, 5).border = { top: THIN, bottom: THIN };
  for (let col = 6; col <= 13; col += 1) {
    ws.getCell(SPACER_ROW, col).border = { top: THIN, bottom: THIN };
  }
  ws.getCell(SPACER_ROW, COL_END).border = { top: THIN, right: THIN, bottom: THIN };
  ws.getRow(SPACER_ROW).height = 30;
}

export function buildOtherStakeholderGroupSheet(workbook, sectionA) {
  const ws = workbook.addWorksheet(SHEET_NAME);
  applyColumnLayout(
    ws,
    {
      A: 9.1796875,
      B: 9.1796875,
      C: 9.1796875,
      D: 6.7265625,
      E: 31.453125,
      F: 27.81640625,
      G: 25.54296875,
      H: 29.453125,
      I: 25.7265625,
      J: 25.54296875,
      K: 28.7265625,
      L: 25.81640625,
      M: 25.54296875,
      N: 23.26953125,
      O: 9.1796875,
      P: 9.1796875,
    },
    ["A", "B"]
  );
  hideColumnsAfter(ws, 16);
  setupSheetView(ws, { xSplit: 3, ySplit: 10, topLeftCell: "D11", zoomScale: 80 });
  hideRowsThrough(ws, 5);

  addBackLink(ws, COL_END);
  addTitleRow(ws, COL_START, COL_END, "Details of Other Stakeholder Group");

  const fy = Number(sectionA?.listedIdentity?.financialYear) || new Date().getFullYear();

  setCell(ws, HEADER_ROW_1, COL_START, "Sr. No.", {
    font: { ...HEADER_FONT_BOLD_11, size: 12 },
    bordered: false,
  });
  setCell(ws, HEADER_ROW_1, 5, "Stakeholder group from whom complaint is received", {
    font: HEADER_FONT_BOLD_11,
    align: "center",
    bordered: false,
  });
  setCell(ws, HEADER_ROW_1, 6, "Grievance Redressal Mechanism in Place (Yes/No/NA)", {
    font: HEADER_FONT_BOLD_11,
    align: "center",
    bordered: false,
  });
  setCell(ws, HEADER_ROW_1, 7, "(If Yes, then provide web-link for grievance redress policy)", {
    font: HEADER_FONT_BOLD_11,
    align: "center",
    bordered: false,
  });
  setCell(ws, HEADER_ROW_1, 8, `FY (${formatFyShort(fy)})`, {
    font: HEADER_FONT_BOLD_11,
    bordered: false,
  });
  setCell(ws, HEADER_ROW_1, 11, `PY (${formatFyShort(fy - 1)})`, {
    font: HEADER_FONT_BOLD_11,
    bordered: false,
  });
  setCell(ws, HEADER_ROW_1, COL_END, "(If NA, then provide the reason)", {
    font: HEADER_FONT_BOLD_11,
    align: "center",
    bordered: false,
  });

  [
    { col: 8, label: "Number of complaints filed during the year" },
    { col: 9, label: "Number of complaints pending resolution at close of the year" },
    { col: 10, label: "Remarks" },
    { col: 11, label: "Number of complaints filed during the year" },
    { col: 12, label: "Number of complaints pending resolution at close of the year" },
    { col: 13, label: "Remarks" },
    { col: COL_END, label: "(If NA, then provide the reason)" },
  ].forEach(({ col, label }) => {
    setCell(ws, HEADER_ROW_2, col, label, {
      font: HEADER_FONT_BOLD_11,
      align: "center",
      bordered: false,
    });
  });

  borderComplaintsHeaderRow1(ws, HEADER_ROW_1);
  borderComplaintsHeaderRow2(ws, HEADER_ROW_2);

  ws.mergeCells(HEADER_ROW_1, COL_START, HEADER_ROW_2, COL_START);
  ws.mergeCells(HEADER_ROW_1, 5, HEADER_ROW_2, 5);
  ws.mergeCells(HEADER_ROW_1, 6, HEADER_ROW_2, 6);
  ws.mergeCells(HEADER_ROW_1, 7, HEADER_ROW_2, 7);
  ws.mergeCells(HEADER_ROW_1, 8, HEADER_ROW_1, 10);
  ws.mergeCells(HEADER_ROW_1, 11, HEADER_ROW_1, 13);
  ws.mergeCells(HEADER_ROW_1, COL_END, HEADER_ROW_2, COL_END);
  ws.getRow(HEADER_ROW_2).height = 43.5;

  borderDataRow(ws, TEMPLATE_ROW);
  for (let col = COL_START; col <= COL_END; col += 1) {
    const fill = col === 7 || col === COL_END ? NA_FILL : undefined;
    setCell(ws, TEMPLATE_ROW, col, "", { bordered: false, fill });
  }
  ws.getRow(TEMPLATE_ROW).height = 15.5;
  ws.getRow(TEMPLATE_ROW).hidden = true;

  addSpacerRow(ws);

  const items = normalizeItems(sectionA);
  items.forEach((item, idx) => {
    const row = DATA_START_ROW + idx;
    const current = item?.currentFinancialYear || item;
    const previous = item?.previousFinancialYear || item;
    const weblink = safeText(item?.grievanceRedressalWeblink || item?.webLink);

    borderDataRow(ws, row);
    setCell(ws, row, COL_START, String(idx + 1), { align: "center", bordered: false, fill: undefined, font: BODY_FONT });
    setCell(ws, row, 5, safeText(item?.stakeholderGroup || item?.name, "Other"), {
      align: "center",
      bordered: false,
      fill: VALUE_FILL,
    });
    setCell(
      ws,
      row,
      6,
      resolveGrievanceMechanism(
        weblink,
        item?.grievanceMechanism || item?.grievanceRedressalMechanismInPlace
      ),
      { align: "center", bordered: false, fill: VALUE_FILL }
    );
    setCell(ws, row, 7, weblink, { align: "center", bordered: false, fill: VALUE_FILL });
    setCell(ws, row, 8, safeNum(current?.filed), { align: "right", bordered: false, fill: VALUE_FILL });
    setCell(ws, row, 9, safeNum(current?.pending), { align: "right", bordered: false, fill: VALUE_FILL });
    setCell(ws, row, 10, safeText(current?.remarks), { align: "center", bordered: false, fill: VALUE_FILL });
    setCell(ws, row, 11, safeNum(previous?.filed), { align: "right", bordered: false, fill: VALUE_FILL });
    setCell(ws, row, 12, safeNum(previous?.pending), { align: "right", bordered: false, fill: VALUE_FILL });
    setCell(ws, row, 13, safeText(previous?.remarks), { align: "center", bordered: false, fill: VALUE_FILL });
    setCell(ws, row, COL_END, safeText(item?.naReason), {
      align: "center",
      bordered: false,
      fill: NA_FILL,
    });
    ws.getRow(row).height = 43.5;
  });

  hideRowsThrough(ws, 5);
  return ws;
}
